/**
 * AS PALAVRAS QUE SURGEM DE CIMA PARA BAIXO, DENTRO DA PRÓPRIA LINHA.
 *
 * ── O PONTO, E ELE VEM DECIDIDO PELAS DUAS PONTAS (`T6.1`) ───────────────────────────────────
 *
 * Ele, na letra: *"Eu estilizaria um pouco mais (…) ter um aspecto um pouco melhor de animação.
 * Pra essa página inicial ser bem chamativa — **não aquelas animações com delay**, mas animações
 * tipo **palavras surgindo de cima pra baixo dentro da própria linha**, essas coisas que deixam o
 * site mais interessante de olhar pra uma pessoa que acessa e parece refinado."*
 *
 * O dossiê registra que isto **não é dúvida**: *"você já decidiu em `[P01]`, e decidiu pelas duas
 * pontas"*. Então há um SIM e um NÃO, e o não é tão instrução quanto o sim.
 *
 * ── O QUE "NÃO AQUELAS ANIMAÇÕES COM DELAY" QUER DIZER, E O QUE EU FIZ COM ISSO ──────────────
 *
 * O padrão que ele está vetando é reconhecível: o conteúdo **não está lá** quando a página abre, e
 * aparece depois — às vezes por rolagem, às vezes por temporizador. Quem chega vê uma página vazia
 * e espera. É a coisa que ele chama de delay.
 *
 * Isso virou uma trava de desenho, e é a decisão mais importante deste arquivo: **o texto tem que
 * estar legível mesmo se a animação não rodar.** Nada aqui começa em `opacity: 0` por JavaScript;
 * o estado natural do HTML é o estado FINAL, e a animação só empurra a palavra de fora para dentro
 * a partir dele (`animation-fill-mode: backwards`). Se o CSS não carregar, se a animação for
 * ignorada, se o navegador for antigo — a frase está inteira na tela.
 *
 * O contrário — animar para dentro a partir de `opacity: 0` — é o que faz o padrão que ele odeia
 * ser tão ruim: quando ele falha, falha escondendo o conteúdo.
 *
 * ── E A PALAVRA TEM QUE SURGIR "DENTRO DA PRÓPRIA LINHA" ─────────────────────────────────────
 *
 * Esta parte é literal e é o que dá o efeito: cada palavra viaja **na vertical, dentro de uma
 * janela do tamanho da própria linha**. O `overflow: hidden` da linha é o que faz a palavra
 * *surgir* em vez de *deslizar por cima* — sem ele, a palavra apareceria sobre o que estiver acima,
 * que é um efeito diferente e mais barato.
 *
 * Por isso a marcação é em três níveis, e nenhum deles é decoração:
 *
 *     <span class="ps-linha">        ← a janela: overflow hidden, uma por linha
 *       <span class="ps-palavra">    ← o que viaja
 *
 * ── POR QUE A DIVISÃO EM PALAVRAS É UM MÓDULO SEPARADO E TESTADO ─────────────────────────────
 *
 * Porque quebrar texto em palavras é onde se perde acento, espaço duplo e pontuação — e o título
 * da home é a primeira coisa que alguém lê do produto. `palavras-da-linha.ts` cobra que a volta
 * seja exata.
 */
import { palavrasDaLinha } from "./palavras-da-linha";
import "./palavras-que-surgem.scss";

export interface PropsPalavrasQueSurgem {
  /** Uma entrada por linha. As linhas NÃO se juntam: cada uma é uma janela própria. */
  linhas: string[];
  /**
   * Quanto cada palavra espera depois da anterior, em milissegundos.
   *
   * Pequeno de propósito. Um passo grande transforma isto na animação que ele vetou: com 200 ms e
   * seis palavras, a última chega em 1,2 s — e aí é "delay", com outro nome. 45 ms dá um
   * escalonamento que se lê como movimento único, e a frase inteira assenta em menos de meio
   * segundo.
   */
  passoMs?: number;
}

/** O passo entre palavras. Ver `passoMs` para por que é este número e não um maior. */
export const PASSO_PADRAO_MS = 45;

export default function PalavrasQueSurgem({ linhas, passoMs = PASSO_PADRAO_MS }: PropsPalavrasQueSurgem) {
  // A contagem atravessa as linhas: a segunda linha continua de onde a primeira parou, senão as
  // primeiras palavras de cada linha entrariam todas juntas e o movimento perderia a direção.
  let ordem = 0;

  return (
    <>
      {linhas.map((linha, i) => (
        <span className="ps-linha" key={i}>
          {palavrasDaLinha(linha).map((palavra, j) => (
            <span
              className="ps-palavra"
              key={j}
              style={{ animationDelay: `${ordem++ * passoMs}ms` }}
            >
              {palavra}
            </span>
          ))}
        </span>
      ))}
    </>
  );
}
